// Docked DevTools panel for the session page. Owns the embedded
// <iframe> lifecycle, a drag-to-resize top edge whose height persists per
// browser, and a pop-out window that mirrors real DevTools "undock into a
// separate window". Popping out removes the iframe so its CDP socket closes;
// docking back re-embeds a fresh iframe.
const STORAGE_KEY = "devtools-panel-height";
const MIN_HEIGHT = 220;
const MAX_VIEWPORT_RATIO = 0.92;
const DEFAULT_VIEWPORT_RATIO = 0.6;
const POPOUT_POLL_MS = 400;
const POPOUT_WIDTH = 1280;
const POPOUT_HEIGHT = 860;
const POPPED_HEIGHT = 44;

function maxHeight() {
  return Math.round(window.innerHeight * MAX_VIEWPORT_RATIO);
}

function clampHeight(px) {
  return Math.min(Math.max(px, MIN_HEIGHT), maxHeight());
}

function storedHeight() {
  try {
    const parsed = Number.parseInt(localStorage.getItem(STORAGE_KEY), 10);
    return Number.isFinite(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

function persistHeight(px) {
  try {
    localStorage.setItem(STORAGE_KEY, String(px));
  } catch {
    // localStorage may be unavailable (e.g. private mode); ignore.
  }
}

export const DevToolsPanel = {
  mounted() {
    this.url = this.el.dataset.devtoolsUrl;
    this.viewport = this.el.querySelector("[data-devtools-viewport]");
    this.poppedBar = this.el.querySelector("[data-devtools-popped]");
    this.blockedHint = this.el.querySelector("[data-devtools-blocked]");
    this.resizeHandle = this.el.querySelector("[data-devtools-handle]");
    this.popoutBtn = this.el.querySelector("[data-devtools-popout]");
    this.dockBtn = this.el.querySelector("[data-devtools-dock]");
    this.iframe = null;
    this.popped = null;
    this.pollId = null;

    const session = new URL(this.url, window.location.origin).searchParams.get("session");
    this.popoutName = `tvlabs-devtools-${session || "default"}`;

    const initial = storedHeight() ?? Math.round(window.innerHeight * DEFAULT_VIEWPORT_RATIO);
    this.el.style.height = `${clampHeight(initial)}px`;

    this.embedIframe();
    this.bindResize();
    this.bindControls();

    this.onWindowResize = () => {
      if (this.popped) return;
      this.el.style.height = `${clampHeight(this.el.offsetHeight)}px`;
    };
    window.addEventListener("resize", this.onWindowResize);
  },

  destroyed() {
    this.teardown();
  },

  embedIframe() {
    if (this.iframe || !this.viewport) return;
    const iframe = document.createElement("iframe");
    iframe.src = this.url;
    iframe.title = "DevTools";
    iframe.className = "absolute inset-0 h-full w-full border-0 bg-white";
    this.viewport.appendChild(iframe);
    this.iframe = iframe;
  },

  removeIframe() {
    if (!this.iframe) return;
    this.iframe.remove(); // dropping the node closes its CDP websocket
    this.iframe = null;
  },

  bindResize() {
    if (!this.resizeHandle) return;

    this.onPointerMove = (event) => {
      const next = clampHeight(this.dragStartHeight + (this.dragStartY - event.clientY));
      this.el.style.height = `${next}px`;
    };

    this.endDrag = (event) => {
      this.resizeHandle.releasePointerCapture?.(event.pointerId);
      window.removeEventListener("pointermove", this.onPointerMove);
      window.removeEventListener("pointerup", this.endDrag);
      window.removeEventListener("pointercancel", this.endDrag);
      document.body.style.userSelect = "";
      document.body.style.cursor = "";
      if (this.iframe) this.iframe.style.pointerEvents = "";
      persistHeight(this.el.offsetHeight);
    };

    this.onPointerDown = (event) => {
      if (event.button !== 0) return;
      event.preventDefault();
      this.dragStartY = event.clientY;
      this.dragStartHeight = this.el.offsetHeight;
      this.resizeHandle.setPointerCapture?.(event.pointerId);
      document.body.style.userSelect = "none";
      document.body.style.cursor = "row-resize";
      // Let the drag glide over the iframe instead of being swallowed by it.
      if (this.iframe) this.iframe.style.pointerEvents = "none";
      window.addEventListener("pointermove", this.onPointerMove);
      window.addEventListener("pointerup", this.endDrag);
      window.addEventListener("pointercancel", this.endDrag);
    };

    this.onHandleKey = (event) => {
      const step = event.shiftKey ? 48 : 16;
      if (event.key === "ArrowUp") {
        this.el.style.height = `${clampHeight(this.el.offsetHeight + step)}px`;
      } else if (event.key === "ArrowDown") {
        this.el.style.height = `${clampHeight(this.el.offsetHeight - step)}px`;
      } else {
        return;
      }
      event.preventDefault();
      persistHeight(this.el.offsetHeight);
    };

    this.resizeHandle.addEventListener("pointerdown", this.onPointerDown);
    this.resizeHandle.addEventListener("keydown", this.onHandleKey);
  },

  bindControls() {
    this.onPopout = () => this.popOut();
    this.onDock = () => this.dockBack();
    this.popoutBtn?.addEventListener("click", this.onPopout);
    this.dockBtn?.addEventListener("click", this.onDock);
  },

  popOut() {
    if (this.popped && !this.popped.closed) {
      this.popped.focus();
      return;
    }

    // A page target accepts one debugger client, so drop the docked iframe
    // before the popout attaches.
    this.removeIframe();

    const left = Math.max(0, Math.round((window.screen.availWidth - POPOUT_WIDTH) / 2));
    const top = Math.max(0, Math.round((window.screen.availHeight - POPOUT_HEIGHT) / 2));
    const features = `popup=yes,width=${POPOUT_WIDTH},height=${POPOUT_HEIGHT},left=${left},top=${top}`;
    const win = window.open(this.url, this.popoutName, features);

    if (!win || win.closed) {
      this.embedIframe();
      this.flashBlockedHint();
      return;
    }

    this.popped = win;
    this.dockedHeight = this.el.offsetHeight;
    this.el.style.height = `${POPPED_HEIGHT}px`;
    this.setPopped(true);
    win.focus();
    this.pollId = window.setInterval(() => {
      if (this.popped && this.popped.closed) this.redock();
    }, POPOUT_POLL_MS);
  },

  dockBack() {
    if (this.popped && !this.popped.closed) this.popped.close();
    this.redock();
  },

  redock() {
    if (this.pollId !== null) {
      window.clearInterval(this.pollId);
      this.pollId = null;
    }
    this.popped = null;
    if (this.dockedHeight) this.el.style.height = `${this.dockedHeight}px`;
    this.embedIframe();
    this.setPopped(false);
  },

  setPopped(isPopped) {
    if (this.poppedBar) this.poppedBar.hidden = !isPopped;
    if (this.viewport) this.viewport.hidden = isPopped;
    // No resizing a collapsed strip; hiding the handle also blocks the drag/key paths.
    if (this.resizeHandle) this.resizeHandle.hidden = isPopped;
  },

  flashBlockedHint() {
    if (!this.blockedHint) return;
    this.blockedHint.hidden = false;
    window.clearTimeout(this.blockedTimer);
    this.blockedTimer = window.setTimeout(() => {
      this.blockedHint.hidden = true;
    }, 5000);
  },

  teardown() {
    if (this.pollId !== null) window.clearInterval(this.pollId);
    window.clearTimeout(this.blockedTimer);
    window.removeEventListener("resize", this.onWindowResize);
    window.removeEventListener("pointermove", this.onPointerMove);
    window.removeEventListener("pointerup", this.endDrag);
    window.removeEventListener("pointercancel", this.endDrag);
    this.resizeHandle?.removeEventListener("pointerdown", this.onPointerDown);
    this.resizeHandle?.removeEventListener("keydown", this.onHandleKey);
    this.popoutBtn?.removeEventListener("click", this.onPopout);
    this.dockBtn?.removeEventListener("click", this.onDock);
    document.body.style.userSelect = "";
    document.body.style.cursor = "";
    if (this.popped && !this.popped.closed) this.popped.close();
    this.popped = null;
    this.removeIframe();
  },
};
